<template>
    <div class="flex relative space-x-2">
        <div
            @click="showSetLnkModal"
            :class="[
              hyperlinkStyles,
              'cursor-pointer flex items-center justify-center select-none w-6 h-6 rounded-md bg-[#F4F4F4]',
            ]" 
        >
            <LinkIcon
                :color="fillLinkColor"
            />
        </div>

        <div v-if="setLnkModal || isVisibleEditLnk" v-click-outside="handleClickOutside" style="margin-left: -180px" class="absolute inset-x-0 flex justify-start px-2 items-center mt-12 w-[360px] h-[72px] rounded shadow-lg px-[20px] bg-[#FFFFFF] border border-[#DBDBDC] z-40 -ml-[180px]">
            <div class="flex justify-start items-center w-full">
                <div class="relative mt-1 rounded-md shadow-sm w-[90%]">
                    
                    <div class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                        <MagnifyingGlassIcon/>
                    </div>
                    
                    <input
                        type="text"
                        ref="lnkInput"
                        v-model="url"
                        class="block w-full rounded-md border-gray-300 pl-8 pr-4 focus:border-[#2A8176] focus:ring-0 sm:text-sm"
                        placeholder="Type or paste link"
                    />
                </div>
                
                <button
                    type="button"
                    @click="handleApply"
                    :class="[
                        'inline-flex justify-center font-[Roboto] bg-white px-4 py-2 text-sm font-medium focus:outline-none focus:ring-0 sm:ml-3',
                        {'text-[#2A8176]': isValidUrl, 'text-[#C2C4C6]': !isValidUrl}
                    ]"
                >
                    Apply
                </button>
            </div>
        </div>
    </div>
</template>
  
<script setup>
import { useTiptapStore } from '../../store/tiptap.store'
import { storeToRefs } from 'pinia'
import { computed, ref, watch, onMounted, nextTick } from 'vue'
import { isValidHttpUrl, validateEmail } from '../../utils/helpers'

const tiptap = useTiptapStore()
const { editor, isFocused, isVisibleEditLnk } = storeToRefs(tiptap)
const url = ref(editor?.value?.getAttributes('link')?.href)
const lnkInput = ref(null)

import MagnifyingGlassIcon from "../../icons/pdf-hyperlink/magnifying-glass-icon.vue";
import LinkIcon from "../../icons/pdf-hyperlink/link-icon.vue"

// used for button color
const isValidUrl = ref(false)
watch(url, newval => {
    isValidUrl.value = isValidHttpUrl(newval) || validateEmail(newval)
})

// returns current selection for focused editor
const getCurrentSelection = () => {
    return editor.value ? editor.value.view.state.selection.empty : null
}

// tooltip styles
const fillLinkColor = computed(() => isFocused.value && editor.value.isActive('link') ? '#2A8176' : '#444C58')
const hyperlinkStyles = computed(() => ({
    'border-b border-[#2A8176]': isFocused.value && editor.value.isActive('link')
}))

// working with setLnk modal variable
const setLnkModal = ref(false)

const showSetLnkModal = () => {
    // if the selected text already have link than remove the link
    if(editor?.value?.isActive('link')) {
        editor.value.commands.unsetLink()
    } else {
        // if selected text does not have a link than show modal to enter the URL if not empty the selection
        const currentSelectionIsEmpty = getCurrentSelection() ?? true
        if(currentSelectionIsEmpty) return
        setLnkModal.value = true

        // set autofocus
        nextTick(() => {
            lnkInput.value.focus()
        })
    }
}
const hideSetLnkModal = () => {
    setLnkModal.value = false
    tiptap.hideVisibleEditLnk()
}

const handleClickOutside = () => {
    hideSetLnkModal()
}

watch([setLnkModal, isVisibleEditLnk], mode => {
    url.value = mode===false ? null : (editor.value ? editor.value.getAttributes('link').href : null)
})

onMounted(() => {
  document.addEventListener('keydown', e => {
    if (e.key === "k" && (e.ctrlKey || e.metaKey)) {
        e.preventDefault()
        const currentSelectionIsEmpty = getCurrentSelection() ?? true
        if(currentSelectionIsEmpty) return

        // that function open modal or if selected text remove link of selected text
        showSetLnkModal()
    }
  })
})

// submit the URL
const handleApply = () => {
    if(! isValidUrl.value && url.value) return

    // cancelled
    if (url.value === null) {
        return
    }

    // empty
    if (url.value === '') {
        editor.value
        .chain()
        .focus()
        .extendMarkRange('link')
        .unsetLink()
        .run()

        return
    }

    // if user not add http and is not email value than add prefix https
    if(!/(http(s?)):\/\//i.test(url.value) && !validateEmail(url.value)) {
        url.value = 'https://' + url.value
    }

    // update link
    editor.value
        .chain()
        .focus()
        .extendMarkRange('link')
        .setLink({ href: url.value })
        .run()

    hideSetLnkModal()
}
</script>

